import {
  DndContext,
  DragOverlay,
  closestCenter,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { IoLockClosed, IoSearch, IoTrashOutline } from 'react-icons/io5';
import { MdDragHandle } from 'react-icons/md';

import { Modal } from '@/components';
import { Button, ButtonOutlined } from '@/components/Button';
import ContextMenu from '@/components/ContextMenu/ContextMenu';
import { useLongPress } from '@/hooks/useLongPress';
import { getWidgetDef } from '@/utils/dashboard.registry';
import {
  CATEGORY_ICONS,
  type WidgetCategory,
  type WidgetId,
  type WidgetLayoutItem,
} from '@/utils/dashboard.types';

import sm from '../DashboardSettingsPage.mobile.module.scss';
import s from '../DashboardSettingsPage.module.scss';

import { WidgetCategoryList } from './WidgetCategoryList';

type DashboardSettingsMobilePageProps = {
  groupedWidgets: Record<WidgetCategory, WidgetLayoutItem[]>;
  lockedVisibleItems: WidgetLayoutItem[];
  sortableVisibleItems: WidgetLayoutItem[];
  sortableVisibleIds: string[];
  lockedIds: Set<WidgetId>;
  activeId: string | null;
  isAddModalOpen: boolean;
  hiddenWidgetsCount: number;
  loading: boolean;
  sensors: ReturnType<typeof import('@dnd-kit/core').useSensors>;
  onToggle: (id: string) => void;
  onDragStart: (event: DragStartEvent) => void;
  onDragOver: (event: DragOverEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onOpenAddModal: () => void;
  onCloseAddModal: () => void;
  onCancel: () => void;
  onSave: () => void | Promise<void>;
};

function SortableMobileWidgetRow({
  item,
  onRemove,
}: {
  item: WidgetLayoutItem;
  onRemove: (id: string) => void;
}) {
  const def = getWidgetDef(item.id);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);

  const longPressHandlers = useLongPress(() => setMenuOpen(true));

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  if (!def) return null;

  if (isDragging) {
    return <div ref={setNodeRef} style={style} className={sm.mobileWidgetPlaceholder} />;
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${sm.mobileWidgetRow} ${menuOpen ? sm.mobileWidgetRowActive : ''}`}
    >
      <div ref={setBodyEl} className={sm.mobileWidgetBody} {...longPressHandlers}>
        <span className={sm.mobileWidgetIconWrapper}>{CATEGORY_ICONS[def.category]}</span>
        <span className={sm.mobileWidgetLabel}>{def.label}</span>
      </div>
      <button
        type="button"
        className={sm.mobileDragHandleButton}
        aria-label={`Reorder ${def.label}`}
        {...attributes}
        {...listeners}
      >
        <MdDragHandle size={20} className={s.dragHandleIcon} />
      </button>
      <ContextMenu
        items={[
          {
            label: 'Delete',
            variant: 'danger',
            icon: <IoTrashOutline size={18} />,
            onClick: () => onRemove(item.id),
          },
        ]}
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        anchorEl={bodyEl}
      />
    </div>
  );
}

function LockedMobileWidgetRow({ item }: { item: WidgetLayoutItem }) {
  const def = getWidgetDef(item.id);
  if (!def) return null;

  return (
    <div className={`${sm.mobileWidgetRow} ${sm.mobileWidgetRowLocked}`}>
      <div className={sm.mobileWidgetBody}>
        <span className={sm.mobileWidgetIconWrapper}>{CATEGORY_ICONS[def.category]}</span>
        <span className={sm.mobileWidgetLabel}>{def.label}</span>
      </div>
      <span className={sm.mobileLockIcon} aria-label="Locked">
        <IoLockClosed size={18} />
      </span>
    </div>
  );
}

function MobileWidgetOverlay({ item }: { item: WidgetLayoutItem }) {
  const def = getWidgetDef(item.id);
  if (!def) return null;

  return (
    <div className={sm.mobileWidgetOverlay}>
      <span className={sm.mobileWidgetIconWrapper}>{CATEGORY_ICONS[def.category]}</span>
      <span className={sm.mobileWidgetLabel}>{def.label}</span>
      <div className={sm.mobileDragHandleButton}>
        <MdDragHandle size={20} className={s.dragHandleIcon} />
      </div>
    </div>
  );
}

export function DashboardSettingsMobilePage({
  groupedWidgets,
  lockedVisibleItems,
  sortableVisibleItems,
  sortableVisibleIds,
  lockedIds,
  activeId,
  isAddModalOpen,
  hiddenWidgetsCount,
  loading,
  sensors,
  onToggle,
  onDragStart,
  onDragOver,
  onDragEnd,
  onOpenAddModal,
  onCloseAddModal,
  onCancel,
  onSave,
}: DashboardSettingsMobilePageProps) {
  const [modalSearch, setModalSearch] = useState('');

  return (
    <div className={sm.pageMobile}>
      <div className={sm.sectionMobile}>
        <div className={sm.mobileHeader}>
          <h3 className={s.previewTitle}>Mobile Dashboard</h3>
        </div>
        <p className={s.previewHint}>Please drag to reorder the visible sections.</p>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          // The page is the only scroller now, so dragging past the fold has to
          // scroll it — with the list's own max-height gone, a layout longer
          // than the screen would otherwise be unreorderable below the fold.
          autoScroll
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
        >
          <div className={sm.mobileSelectedList}>
            {lockedVisibleItems.map((item) => (
              <LockedMobileWidgetRow key={item.id} item={item} />
            ))}
            <SortableContext items={sortableVisibleIds} strategy={verticalListSortingStrategy}>
              {sortableVisibleItems.map((item) => (
                <SortableMobileWidgetRow key={item.id} item={item} onRemove={onToggle} />
              ))}
            </SortableContext>
            <div className={sm.mobileAddSection}>
              <p className={sm.mobileHiddenHint}>
                {hiddenWidgetsCount > 0
                  ? `${hiddenWidgetsCount} hidden widget${hiddenWidgetsCount > 1 ? 's' : ''}`
                  : 'All widgets are visible.'}
              </p>
              <button type="button" className={sm.mobileAddButton} onClick={onOpenAddModal}>
                + Add Widget
              </button>
            </div>
          </div>
          {createPortal(
            // Portal to the body so the fixed-positioned overlay resolves
            // against the viewport. Rendered in place it resolves against the
            // nearest ancestor that establishes a containing block instead —
            // the settings pane this list is embedded in does — which left the
            // dragged row sitting a pane's offset below the finger. Same fix
            // the desktop grid already carries.
            <DragOverlay zIndex={10000}>
              {activeId ? (
                <MobileWidgetOverlay item={sortableVisibleItems.find((w) => w.id === activeId)!} />
              ) : null}
            </DragOverlay>,
            document.body,
          )}
        </DndContext>

        <Modal open={isAddModalOpen} onClose={onCloseAddModal} title="Manage Mobile Sections">
          <div className={sm.modalContent}>
            <label className={s.searchInput}>
              <IoSearch className={s.searchIcon} />
              <input
                type="text"
                placeholder="Search widgets"
                value={modalSearch}
                onChange={(e) => setModalSearch(e.target.value)}
              />
            </label>
            <div className={sm.modalListScroll}>
              <WidgetCategoryList
                groupedWidgets={groupedWidgets}
                onToggle={onToggle}
                lockedIds={lockedIds}
                searchQuery={modalSearch}
              />
            </div>
          </div>
        </Modal>

        <div className={sm.actions}>
          <Button onClick={onSave} disabled={loading}>
            {loading ? 'Saving…' : 'Save Changes'}
          </Button>
          <ButtonOutlined onClick={onCancel} disabled={loading}>
            Cancel
          </ButtonOutlined>
        </div>
      </div>
    </div>
  );
}
